import Table, { TableBody, TableHeader } from '@ingka/table';
import Text from '@ingka/text';
import type { Lottery } from '@lotta/shared/types-lottery-schemas';
import { getFormattedNbrDraws, getTotalDrawsForLottery } from '@lotta/shared/util-draws';
import {
  useFormatWithLocale,
  useNavigateWithLocale,
} from '@lotta/self-service/lottery-fe/feature-hooks';
import { useTranslation } from 'react-i18next';
import { ApprovalStatus } from '../approval-status/ApprovalStatus';
import { LotteryStatus } from '../lottery-status/LotteryStatus';
import { getFormattedPeriod, getFormattedType } from '../../utils/format-utils';
import styles from './LotteryTable.module.scss';

export type LotteryTableProps = {
  lotteries: Lottery[];
  timeZone: string;
};

type TableRowsProps = { lotteries: Lottery[]; timeZone: string };
const TableRows: React.FC<TableRowsProps> = ({ lotteries, timeZone }) => {
  const navigate = useNavigateWithLocale();
  const { t } = useTranslation();
  const { dateWithLocale, numberWithLocale } = useFormatWithLocale();

  return (
    <>
      {lotteries.map((lottery) => {
        const { id, name, occurrence, nbrDrawsMade, lotteryStatus, approvalStatus } = lottery;
        const totalDraws = getTotalDrawsForLottery(occurrence);
        const { startDate, endDate } = occurrence;

        return (
          <tr
            className={styles['lottery-table-row']}
            key={id}
            onClick={() => {
              navigate(`/lottery/${id}`);
            }}
          >
            <td>
              <Text className={styles['lottery-table__name']}>{name}</Text>
            </td>
            <td>{t(getFormattedType(occurrence.occurrenceType))}</td>
            <td>
              {t(getFormattedPeriod(occurrence), {
                startDate: dateWithLocale(startDate),
                endDate: dateWithLocale(endDate),
              })}
            </td>
            <td>
              {t(getFormattedNbrDraws(occurrence), {
                nbrDrawsMade: numberWithLocale(nbrDrawsMade),
                totalDraws: numberWithLocale(totalDraws),
              })}
            </td>
            <td>
              <ApprovalStatus approvalStatus={approvalStatus} />
            </td>
            <td>
              <LotteryStatus
                lotteryStatus={lotteryStatus}
                nbrDrawsMade={nbrDrawsMade}
                totalDraws={totalDraws}
                startDate={startDate}
                timeZone={timeZone}
              />
            </td>
          </tr>
        );
      })}
    </>
  );
};

export const LotteryTable = ({ lotteries, timeZone }: LotteryTableProps) => {
  const { t } = useTranslation();
  return (
    <Table fullWidth inset>
      <TableHeader sticky={true}>
        <tr>
          <th>{t('general-name')}</th>
          <th>{t('general-type')}</th>
          <th>{t('general-period')}</th>
          <th>{t('general-draw')}</th>
          <th>{t('general-approval-status')}</th>
          <th>{t('general-lottery-status')}</th>
        </tr>
      </TableHeader>
      <TableBody striped data-testid="lotteries-table-body">
        <TableRows lotteries={lotteries} timeZone={timeZone} />
      </TableBody>
    </Table>
  );
};
